/**
 * hexPreviewCache — one resolved snapshot per alert, shared by every hover on a board.
 *
 * WHY A CACHE AND NOT JUST A useEffect
 * ------------------------------------
 * On a local node an alert's `snapshotUrl` is a directly-loadable URL and the browser's own
 * HTTP cache handles a second look. The CLOUD has no route to a node's image store, so its
 * `loadPreview` blob-fetches the picture through the tunnel and hands back an object URL —
 * uncacheable by the browser, and expensive. A hover card mounts and unmounts with the
 * pointer, so a plain per-mount fetch means one tunnel round-trip every time the mouse
 * crosses a hexagon: sweeping a board of thirty cameras fires thirty requests for pictures
 * nobody waited to see, and the one the operator does settle on still starts from nothing.
 *
 * So resolution is hoisted out of the component. The first hover pays; every hover after it
 * is instant, and a pointer moving across the board coalesces into one request per alert
 * rather than one per mount.
 *
 * OWNERSHIP
 * ---------
 * The cache creates no object URLs itself — `loadPreview` does — but it OUTLIVES the
 * components that display them, so it owns revoking them: a component that revoked on
 * unmount would blank the very entry it just cached. URLs are released only on eviction,
 * oldest first, and only past CAP — so the entry currently on screen (the newest) is never
 * the one released.
 */
import type { HexAlert } from '../../api/contracts';

/**
 * Enough for a sweep across a board plus the alerts in the side panel, and small enough that
 * the blobs behind them are not a leak. Each entry is one JPEG's worth of memory.
 */
const CAP = 24;

/** Insertion-ordered, which is what makes the first key the oldest. */
const resolved = new Map<string, string>();
/** In-flight resolutions, so two hovers of the same hex share one request. */
const pending = new Map<string, Promise<string | null>>();

/**
 * Identity of a PICTURE, not of an alert: `previewReady` bumps when the node reports the
 * snapshot is finally stored, and that is exactly the moment a previous "no image" answer
 * stops being true. Keying on it makes the retry automatic instead of something every caller
 * has to remember.
 */
function keyOf(alert: HexAlert): string {
  return `${alert.id || alert.cameraId}:${alert.previewReady || 0}`;
}

/** The picture if it is already here — what lets a re-hover render with no flicker at all. */
export function cachedPreview(alert: HexAlert): string | null {
  return resolved.get(keyOf(alert)) || null;
}

/**
 * Resolve (or return) this alert's preview. Never throws: a failed or absent preview is a
 * normal state — an alert is broadcast before its image is stored — and resolves as null.
 *
 * A null is deliberately NOT cached: the next `previewReady` bump changes the key anyway, and
 * remembering "no picture" under the current key would outlive the reason for it.
 */
export function resolvePreview(
  alert: HexAlert,
  loadPreview: (alert: HexAlert) => Promise<string | null>,
): Promise<string | null> {
  const key = keyOf(alert);
  const hit = resolved.get(key);
  if (hit) return Promise.resolve(hit);
  const inflight = pending.get(key);
  if (inflight) return inflight;

  const promise = loadPreview(alert)
    .then((url) => {
      if (url) put(key, url);
      return url;
    })
    .catch(() => null)
    .finally(() => {
      pending.delete(key);
    });
  pending.set(key, promise);
  return promise;
}

function put(key: string, url: string) {
  resolved.set(key, url);
  while (resolved.size > CAP) {
    const oldest = resolved.keys().next();
    if (oldest.done) break;
    const stale = resolved.get(oldest.value);
    resolved.delete(oldest.value);
    // Only object URLs come through here (loadPreview's contract), and the cache is the only
    // owner of them, so this cannot pull the picture out from under another component.
    if (stale && stale.startsWith('blob:')) URL.revokeObjectURL(stale);
  }
}
